<template>
  <v-container fluid class="pa-4">
    <v-card class="elevation-4 search-card">
      <v-card-text class="pa-4">
        <v-layout row wrap>
          <!-- Location Search -->
          <v-flex xs12 class="px-2 pb-3">
            <v-autocomplete
              v-model="searchParams.location"
              :items="locationItems"
              :loading="isLoadingLocation"
              :search-input.sync="locationSearchInput"
              :filter="() => true"
              item-text="Name"
              item-value="LocationId"
              hide-no-data
              hide-details
              :label="$t('Hotel-Search-Field-Label')"
              prepend-inner-icon="location_on"
              :placeholder="$t('Hotel-Search-Placeholder')"
              return-object
              outlined
              clearable
              color="primary"
            >
              <template v-slot:item="{ item }">
                <v-list-tile-content>
                  <v-list-tile-title class="font-weight-medium" v-text="item.Name"></v-list-tile-title>
                  <v-list-tile-sub-title class="grey--text" v-text="item.Type || item.CountryName"></v-list-tile-sub-title>
                </v-list-tile-content>
              </template>
            </v-autocomplete>
          </v-flex>

          <!-- Dates and Nights -->
          <v-flex xs12 sm6 md3 class="px-2 pb-3">
            <v-menu
              v-model="menuStartDate"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-model="searchParams.startDate"
                  :label="$t('Hotel-Check-In-Date')"
                  prepend-inner-icon="event"
                  readonly
                  outlined
                  hide-details
                  v-on="on"
                  color="primary"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="searchParams.startDate"
                @input="onStartDateChange"
                :min="today"
                color="primary"
                locale="tr-TR"
              ></v-date-picker>
            </v-menu>
          </v-flex>

          <v-flex xs12 sm6 md2 class="px-2 pb-3">
            <v-text-field
              v-model.number="searchParams.nights"
              :label="$t('Hotel-Nights')"
              type="number"
              min="1"
              outlined
              hide-details
              @input="onNightsChange"
              prepend-inner-icon="nights_stay"
              color="primary"
            ></v-text-field>
          </v-flex>

          <v-flex xs12 sm6 md3 class="px-2 pb-3">
            <v-menu
              v-model="menuEndDate"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-model="searchParams.endDate"
                  :label="$t('Hotel-Check-Out-Date')"
                  prepend-inner-icon="event"
                  readonly
                  outlined
                  hide-details
                  v-on="on"
                  color="primary"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="searchParams.endDate"
                @input="onEndDateChange"
                :min="searchParams.startDate"
                color="primary"
                locale="tr-TR"
              ></v-date-picker>
            </v-menu>
          </v-flex>

          <!-- Room Selection Dialog Activator -->
          <v-flex xs12 sm6 md4 class="px-2 pb-3">
            <v-text-field
              :value="roomSummary"
              :label="$t('Hotel-Room-Passenger-Info')"
              prepend-inner-icon="hotel"
              readonly
              outlined
              hide-details
              @click="roomDialog = true"
              class="cursor-pointer"
              color="primary"
            ></v-text-field>
          </v-flex>
          <!-- Nationality -->
          <v-flex xs12 sm6 md3 class="px-2 pb-3">
            <v-autocomplete
              v-model="searchParams.nationality"
              :items="nationalities"
              item-text="name"
              item-value="code"
              :label="$t('Hotel-Nationality')"
              prepend-inner-icon="public"
              outlined
              hide-details
              color="primary"
            ></v-autocomplete>
          </v-flex>

          <!-- Currency -->
          <v-flex xs12 sm6 md3 class="px-2 pb-3">
            <v-select
              v-model="searchParams.currency"
              :items="currencies"
              :label="$t('Hotel-Currency')"
              prepend-inner-icon="payments"
              outlined
              hide-details
              color="primary"
            ></v-select>
          </v-flex>

          <!-- Markup Type -->
          <v-flex xs12 sm6 md3 class="px-2 pb-3">
            <v-select
              v-model="searchParams.markupType"
              :items="markupTypes"
              item-text="text"
              item-value="value"
              :label="$t('Hotel-Markup-Type')"
              prepend-inner-icon="percent"
              outlined
              hide-details
              color="primary"
            ></v-select>
          </v-flex>

          <!-- Markup Value -->
          <v-flex xs12 sm6 md3 class="px-2 pb-3">
            <v-text-field
              v-model.number="searchParams.markupValue"
              :label="$t('Hotel-Markup-Value')"
              type="number"
              outlined
              hide-details
              prepend-inner-icon="attach_money"
              color="primary"
            ></v-text-field>
          </v-flex>
        </v-layout>
      </v-card-text>

      <v-divider></v-divider>
      
      <!-- Search Button Area -->
      <v-card-actions class="pa-2 bg-light">
        <v-spacer></v-spacer>
        <v-btn color="primary" @click="searchHotels" :loading="isSearching" class="px-4 search-btn elevation-2">
          <v-icon left class="mr-1">search</v-icon>
          {{ $t('Search-Hotel') }}
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Room Selection Dialog -->
    <v-dialog v-model="roomDialog" max-width="650px" scrollable>
      <v-card class="room-dialog-card">
        <v-toolbar color="primary" dark flat>
          <v-icon left class="mr-3">king_bed</v-icon>
          <v-toolbar-title class="headline">{{ $t('Hotel-Room-Passenger-Selection') }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="roomDialog = false">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>
        
        <v-card-text class="pt-4 bg-grey-lighten-4" style="max-height: 65vh;">
          <v-card v-for="(room, index) in searchParams.rooms" :key="index" class="room-item mb-4 elevation-1">
            <v-card-title class="py-2 px-3 bg-room-header d-flex justify-space-between align-center">
              <span class="subheading font-weight-bold primary--text">
                <v-icon small color="primary" class="mr-2">hotel</v-icon>
                {{ index + 1 }}. {{ $t('Hotel-Room') }}
              </span>
              <v-btn icon small color="error" @click="removeRoom(index)" v-if="searchParams.rooms.length > 1" :title="$t('Hotel-Delete-Room-Tooltip')">
                <v-icon size="20">delete_outline</v-icon>
              </v-btn>
            </v-card-title>
            <v-divider></v-divider>
            
            <v-card-text class="pa-3">
              <v-layout row wrap align-center>
                <v-flex xs12 sm6 class="px-2 mb-2">
                  <v-select
                    v-model="room.adults"
                    :items="[1, 2, 3, 4, 5]"
                    :label="$t('Hotel-Adult-Count')"
                    outlined
                    dense
                    hide-details
                    prepend-inner-icon="person"
                  ></v-select>
                </v-flex>
                <v-flex xs12 sm6 class="px-2 mb-2">
                  <v-select
                    v-model="room.children"
                    :items="[0, 1, 2, 3, 4]"
                    :label="$t('Hotel-Child-Count')"
                    @change="updateChildrenAges(room)"
                    outlined
                    dense
                    hide-details
                    prepend-inner-icon="child_care"
                  ></v-select>
                </v-flex>
                
                <!-- Child Ages -->
                <v-flex xs12 v-if="room.children > 0" class="mt-3">
                  <div class="caption font-weight-bold grey--text text--darken-1 mb-2 px-2">
                    <v-icon size="16" class="mr-2">info</v-icon>
                    {{ $t('Hotel-Select-Child-Ages') }}
                  </div>
                  <v-layout row wrap>
                    <v-flex xs6 sm4 class="px-2 mb-3" v-for="(age, cIndex) in room.childAges" :key="cIndex">
                      <v-select
                        v-model.number="room.childAges[cIndex]"
                        :items="childAgeOptions"
                        :label="$t('Hotel-Child-Number', {n: cIndex+1})"
                        outlined
                        dense
                        hide-details
                      ></v-select>
                    </v-flex>
                  </v-layout>
                </v-flex>
              </v-layout>
            </v-card-text>
          </v-card>
          
          <v-btn color="secondary" outline block @click="addRoom" class="mt-2 add-room-btn">
            <v-icon left class="mr-2">add_circle_outline</v-icon> {{ $t('Hotel-Add-New-Room') }}
          </v-btn>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-3 bg-white">
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" flat @click="roomDialog = false">{{ $t('cancel_button') }}</v-btn>
          <v-btn color="primary" @click="roomDialog = false" class="px-4">{{ $t('Hotel-Apply') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-container>
</template>

<script>
import hotelSearchService from './hotelSearchService';

export default {
  props: {
    sessionHeader: {
      type: Object,
      required: true
    }
  },
  data() {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    return {
      isLoadingLocation: false,
      locationSearchInput: null,
      locationItems: [],
      locationTimeout: null,

      menuStartDate: false,
      menuEndDate: false,
      today: today.toISOString().substr(0, 10),

      roomDialog: false,
      isSearching: false,

      searchParams: {
        location: null,
        startDate: today.toISOString().substr(0, 10),
        endDate: tomorrow.toISOString().substr(0, 10),
        nights: 1,
        nationality: 'TR',
        currency: 'TRY',
        markupType: 'Amount',
        markupValue: 0,
        rooms: [
          { adults: 2, children: 0, childAges: [] }
        ]
      },

      nationalities: [
        { code: 'TR', name: 'Türkiye' },
        { code: 'US', name: 'United States' },
        { code: 'GB', name: 'United Kingdom' },
        { code: 'DE', name: 'Germany' },
      ],
      currencies: ['TRY', 'USD', 'EUR', 'GBP'],
      markupTypes: [
        { text: this.$t('Amount'), value: 'Amount' },
        { text: this.$t('Percentage'), value: 'Percent' }
      ],
      childAgeOptions: Array.from({ length: 18 }, (_, i) => i) // 0-17
    };
  },
  computed: {
    roomSummary() {
      const totalRooms = this.searchParams.rooms.length;
      let totalAdults = 0;
      let totalChildren = 0;
      this.searchParams.rooms.forEach(r => {
        totalAdults += r.adults;
        totalChildren += r.children;
      });
      let summary = this.$t('Hotel-Room-Summary', {totalRooms, totalAdults});
      if (totalChildren > 0) summary += this.$t('Hotel-Child-Summary', {totalChildren});
      return summary;
    }
  },
  watch: {
    locationSearchInput(val) {
      if (this.searchParams.location && val === this.searchParams.location.Name) {
        return;
      }

      if (val && val.length >= 3) {
        if (this.locationTimeout) clearTimeout(this.locationTimeout);
        this.locationTimeout = setTimeout(() => {
          this.searchLocationFromApi(val);
        }, 500);
      } else {
        this.locationItems = this.searchParams.location ? [this.searchParams.location] : [];
      }
    }
  },
  methods: {
    async searchLocationFromApi(keyword) {
      this.isLoadingLocation = true;
      try {
        const locale = this.$i18n.locale || 'tr';
        const cultureCodes = { 'tr': 'tr-TR', 'en': 'en-US', 'de': 'de-DE', 'fr': 'fr-FR' };
        const currentCulture = cultureCodes[locale] || locale;
        const resp = await hotelSearchService.searchLocation(this.$http, this.sessionHeader, keyword, currentCulture);
        if (resp.body && resp.body.HotelLocations) {
           this.locationItems = resp.body.HotelLocations;
        } else if (resp.data && resp.data.HotelLocations) {
           this.locationItems = resp.data.HotelLocations;
        } else if (resp.body && resp.body.Body && resp.body.Body.Items) {
           this.locationItems = resp.body.Body.Items;
        } else {
           this.locationItems = [];
        }
      } catch (e) {
        console.error('Location search error:', e);
        this.locationItems = [];
      } finally {
        this.isLoadingLocation = false;
      }
    },
    
    onStartDateChange() {
      this.menuStartDate = false;
      this.calculateEndDate();
    },
    onNightsChange() {
      if (this.searchParams.nights < 1) this.searchParams.nights = 1;
      this.calculateEndDate();
    },
    onEndDateChange() {
      this.menuEndDate = false;
      this.calculateNights();
    },
    
    calculateEndDate() {
      if (!this.searchParams.startDate) return;
      const start = new Date(this.searchParams.startDate);
      start.setDate(start.getDate() + parseInt(this.searchParams.nights || 1));
      this.searchParams.endDate = start.toISOString().substr(0, 10);
    },
    calculateNights() {
      if (!this.searchParams.startDate || !this.searchParams.endDate) return;
      const start = new Date(this.searchParams.startDate);
      const end = new Date(this.searchParams.endDate);
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      this.searchParams.nights = diffDays > 0 ? diffDays : 1;
      if (diffDays <= 0) this.calculateEndDate();
    },

    addRoom() {
      this.searchParams.rooms.push({ adults: 2, children: 0, childAges: [] });
    },
    removeRoom(index) {
      this.searchParams.rooms.splice(index, 1);
    },
    updateChildrenAges(room) {
      const currentLength = room.childAges.length;
      if (room.children > currentLength) {
        for (let i = currentLength; i < room.children; i++) {
          room.childAges.push(0);
        }
      } else if (room.children < currentLength) {
        room.childAges.splice(room.children);
      }
    },

    searchHotels() {
      if (!this.searchParams.location) {
        alert(this.$t('Hotel-Select-Location'));
        return;
      }
      this.isSearching = true;

      // Convert childAges to string arrays for API
      const params = JSON.parse(JSON.stringify(this.searchParams));
      params.rooms.forEach(room => {
        room.childAges = room.childAges.map(age => String(age));
      });
      console.log(params,"params");
      this.$emit('search', params);
      setTimeout(() => {
        this.isSearching = false;
      }, 1000);
    }
  }
};
</script>

<style scoped>
.search-card {
  border-radius: 12px;
  overflow: hidden;
}
.search-toolbar {
  border-bottom: 1px solid rgba(0,0,0,0.05);
}
.bg-light {
  background-color: #f9fbfd !important;
}
.cursor-pointer {
  cursor: pointer;
}
.cursor-pointer :deep(input) {
  cursor: pointer !important;
}
.search-btn {
  border-radius: 30px; /* Pill-shaped button */
  text-transform: none;
  font-weight: bold;
  font-size: 1.1rem;
  letter-spacing: 0.5px;
}
.room-item {
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  transition: all 0.3s ease;
}
.room-item:hover {
  box-shadow: 0 4px 12px rgba(0,0,0,0.08) !important;
}
.room-dialog-card {
  border-radius: 12px;
  overflow: hidden;
}
.bg-room-header {
  background-color: #f0f4f8;
}
.bg-grey-lighten-4 {
  background-color: #f5f7fa !important;
}
.bg-white {
  background-color: #ffffff !important;
}
.add-room-btn {
  border-radius: 8px;
  border-style: dashed;
  border-width: 2px;
  text-transform: none;
  font-weight: 600;
}
/* Inputların içindeki ikonlara ve dışındaki ikonlara boşluk veriyoruz */
:deep(.v-input__prepend-inner) {
  padding-right: 12px !important;
}
</style>
